import { Badge, Button, Card, Drawer, Space, Empty, Spin, Typography, Flex } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  NON_ADMIN_NOTIFICATIONS_TYPE,
  NOTIFICATIONS_STATUS_DISPLAY,
  STATUS_COLOR
} from '@/utils/static/kyc';
import { useInView } from 'react-intersection-observer';
import { adminApi, investorApi } from '@/api';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router';
import {
  nonAdminNotificationLink,
  notificationLinkText
} from '@/components/common/modals/Notifications/utils';
import { getLocalizedText } from '@/utils/helpers';
import { usePopups } from '@/hooks';
import { history } from '../../../../routing';

const NotificationItem = ({ log, setSeenNotification, isAdmin, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 1
  });

  useEffect(() => {
    if (inView && !log.isRead) {
      setSeenNotification((prevState) => [...prevState, log.id]);
    }
  }, [inView]);

  return (
    <div ref={ref}>
      <Badge.Ribbon
        text={t(`${NOTIFICATIONS_STATUS_DISPLAY[log.type]}`)}
        color={STATUS_COLOR[log.type]}
      >
        <StyledCard
          title={
            !log.isRead ? (
              <span>
                <Badge status='error' text={t('New')} />
                <span style={{ marginLeft: '8px' }}>
                  {dayjs(log.createdAt).format('DD.MM.YY HH:mm')}
                </span>
              </span>
            ) : (
              <span>{dayjs(log.createdAt).format('DD.MM.YY HH:mm')}</span>
            )
          }
          size='small'
        >
          <Space direction='vertical' size={'small'}>
            {isAdmin && log.investor && log.investorId && (
              <Link to={`/investor/${log.investorId}`}>
                {t('User:')} {log.investor.firstName} {log.investor.lastName}
              </Link>
            )}

            {getLocalizedText(log, 'text', i18n)}

            {NON_ADMIN_NOTIFICATIONS_TYPE[log.type] &&
              (log.investorProductId ||
                log.investorQuestionSectionTypeId ||
                log.requestDocumentId) && (
                <Link
                  to={nonAdminNotificationLink(
                    log.type,
                    log.investorProductId ||
                      log.investorQuestionSectionTypeId ||
                      log.requestDocumentId
                  )}
                >
                  {t(notificationLinkText(log.type))}
                </Link>
              )}

            {isAdmin && log.investorId && log.investorProductId && (
              <Link to={`/investor/${log.investorId}/product/${log.investorProductId}`}>
                {t('Product link')}
              </Link>
            )}

            {isAdmin && log.investorId && log.investorQuestionSectionTypeId && (
              <Link
                to={`/investor/${log.investorId}/questionnaire/${log.investorQuestionSectionTypeId}`}
              >
                {t('Questionnaire link')}
              </Link>
            )}

            {isAdmin && log.investorId && log.requestDocumentId && (
              <Link to={`/investor/${log.investorId}/documents/${log.requestDocumentId}`}>
                {t('Document link')}
              </Link>
            )}

            {isAdmin && log.investorId && log.type === 'REQUEST_REF_CODE' && (
              <Link
                to='#'
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/investor/${log.investorId}`);
                  onClose();
                }}
              >
                {t('Client requests link')}
              </Link>
            )}
          </Space>
        </StyledCard>
      </Badge.Ribbon>
    </div>
  );
};

export const NotificationsModal = ({
  isOpen,
  onClose,
  logs,
  isLoading,
  refetch,
  countUnreadNotifications,
  isAdmin
}) => {
  const { t } = useTranslation();
  const { showErrorToast } = usePopups();
  const [seenNotification, setSeenNotification] = useState([]);

  const seenAllNotification = useMemo(() => {
    if (logs && logs.length) {
      let notificationsArray = [];
      for (const notification of logs) {
        if (!notification.isRead) notificationsArray.push(notification.id);
      }

      return notificationsArray;
    }

    return [];
  }, [logs]);

  const onCloseModal = async (seenNotificationsArray) => {
    if (!!seenNotificationsArray?.length) {
      try {
        const result = isAdmin
          ? await adminApi.readAdminNotifications(seenNotificationsArray)
          : await investorApi.readInvestorNotifications(seenNotificationsArray);
        if (result) refetch();
      } catch (error) {
        console.error(error);
        showErrorToast(error, true);
      }
    }

    onClose();
    setSeenNotification([]);
  };

  return (
    <Drawer
      title={
        <Typography.Title level={4} style={{ marginBottom: 'unset' }}>
          {t('Notifications')}
        </Typography.Title>
      }
      closable={{ 'aria-label': 'Close Button' }}
      onClose={() => onCloseModal(seenNotification)}
      open={isOpen}
      placement={'right'}
      width={500}
      extra={
        <Button
          type='primary'
          onClick={() => onCloseModal(seenAllNotification)}
          disabled={!countUnreadNotifications}
        >
          {t('Read all')}
        </Button>
      }
    >
      {isLoading && (
        <Flex vertical justify='center' align='center'>
          <Spin />
        </Flex>
      )}

      {!!logs && logs.length ? (
        <Space
          direction='vertical'
          size='middle'
          style={{ width: '100%', flexDirection: 'column-reverse' }}
        >
          {logs.map((log, index) => (
            <NotificationItem
              key={index}
              log={log}
              setSeenNotification={setSeenNotification}
              isAdmin={isAdmin}
              onClose={onClose}
            />
          ))}
        </Space>
      ) : (
        <Empty description={t('No notifications yet')} />
      )}
    </Drawer>
  );
};

const StyledCard = styled(Card)`
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition:
    box-shadow 0.3s ease-in-out,
    transform 0.3s ease-in-out;
`;
